import { isReactive, isRef, toRaw, unref } from 'vue'

import type { ProxyHandlerConfig } from './types'

import { buildPath, checkPerformanceGuard, getPropertyType, handleError, logMessage, mergeConfig } from './utils'

/**
 * 深度转换 Proxy 对象为普通对象
 *
 * 这个函数会递归地遍历对象，将所有 Vue 3 的响应式对象（reactive、ref）转换为原始对象。
 * 主要用于解决在 Pinia Store 的 $patch 操作中遇到的 Proxy 相关错误。
 *
 * @template T - 输入对象的类型
 * @param obj - 要转换的对象，可以是任何类型
 * @param config - 转换配置选项
 *   - deep: 是否深度转换嵌套对象（默认: true）
 *   - convertArray: 是否转换数组（默认: true）
 *   - onProxyDetected: 检测到 Proxy 对象时的回调函数
 *   - onConversionError: 转换失败时的回调函数
 *   - warnInConsole: 是否在控制台输出警告（默认: true）
 *   - skipKeys: 这些属性名只剥一层 Proxy，不再递归（默认: []）
 * @param path - 当前处理的路径，用于调试和错误追踪（默认: 'root'）
 * @param depth - 当前递归深度，用于性能保护（默认: 0）
 * @param startTime - 开始处理的时间戳，用于超时检测（默认: Date.now()）
 * @param cache - 已处理对象的 WeakMap 缓存，用于：
 *   - 循环引用检测：如果对象正在处理中，返回原对象避免无限递归
 *   - 重复处理优化：如果对象已经处理过，直接返回缓存的结果
 *   默认会创建新的 WeakMap，通常不需要手动传入
 *
 * @returns 转换后的原始对象，保持原有的数据结构
 *
 * @remarks
 * **性能优化说明：**
 * - 使用 WeakMap 缓存已处理的对象，避免重复转换相同的对象引用
 * - 同时解决循环引用问题：如果对象正在处理中，直接返回原对象
 * - 对于包含大量重复引用的复杂对象结构，性能提升显著
 *
 * **缓存机制说明：**
 * - 每次调用 `deepToRaw` 都会创建新的 WeakMap 缓存
 * - 缓存只在单次调用内有效，确保：
 *   - 同一调用内的重复引用会被优化（避免重复转换）
 *   - 不同调用之间不会共享缓存（避免返回过期的转换结果）
 *   - 如果对象在两次调用之间被修改，第二次调用会重新转换，保证结果正确性
 * - 如果手动传入同一个 cache，需要注意对象可能已被修改的情况
 *
 * @example
 * ```typescript
 * import { reactive } from 'vue'
 * import { deepToRaw } from '@peerup/helper'
 *
 * // 基本用法
 * const reactiveData = reactive({ name: 'test', age: 25 })
 * const rawData = deepToRaw(reactiveData)
 * // 结果: { name: 'test', age: 25 } (普通对象)
 *
 * // 带配置的用法
 * const rawData = deepToRaw(complexObject, {
 *   deep: true,
 *   convertArray: true,
 *   onProxyDetected: (path, value) => {
 *     console.warn(`检测到 Proxy 对象: ${path}`)
 *   },
 *   onConversionError: (path, error) => {
 *     console.error(`转换失败: ${path}`, error)
 *   }
 * })
 *
 * // 在 Pinia Store 中使用
 * const userData = reactive({ name: 'John', profile: reactive({ avatar: 'url' }) })
 * const safeData = deepToRaw(userData)
 * userStore.$patch(safeData) // 安全！
 * ```
 *
 * @throws 当遇到不可转换的 Proxy 对象时可能抛出错误
 * @since 1.0.0
 */
/**
 * 深度转换 Proxy 对象为普通对象（内部实现）
 *
 * 这是 deepToRaw 的内部递归实现，使用已合并的配置避免重复合并
 * 使用 WeakMap 缓存已处理的对象，同时解决循环引用和重复处理问题
 */
function deepToRawInternal<T = unknown>(
  obj: T,
  finalConfig: Required<ProxyHandlerConfig>,
  path: string,
  depth: number,
  startTime: number,
  cache: WeakMap<object, unknown>,
): T {
  // 性能保护检查
  const guard = checkPerformanceGuard(obj, path, depth, startTime)
  if (!guard.shouldContinue) {
    if (finalConfig.warnInConsole) {
      logMessage({
        level: 'warn',
        prefix: 'ProxyHandler',
        message: `性能保护触发: ${guard.reason}`,
        context: path,
      })
    }
    return obj // 返回原对象，避免处理
  }

  // 处理 null 和 undefined（优先检查，避免后续类型检查）
  if (obj == null) {
    return obj
  }

  // 处理基本类型（优先检查，避免对象操作）
  if (typeof obj !== 'object') {
    return obj
  }

  // 检查缓存：如果对象已经处理过，直接返回缓存的结果
  // 这同时解决了循环引用和重复处理的问题：
  // 1. 如果对象已经被处理过，直接返回缓存的结果（避免重复处理）
  // 2. 如果对象正在处理中（循环引用），返回原对象（避免无限递归）
  //
  // 注意：缓存只在单次 deepToRaw 调用内有效，每次调用都会创建新的 WeakMap
  // 这样可以确保：
  // - 同一调用内的重复引用会被优化（避免重复转换）
  // - 不同调用之间不会共享缓存（避免返回过期的转换结果）
  // - 如果对象在两次调用之间被修改，第二次调用会重新转换
  const cached = cache.get(obj as object)
  if (cached !== undefined) {
    // 如果缓存的值等于原对象，说明是循环引用（对象正在处理中）
    if (finalConfig.warnInConsole && cached === obj) {
      logMessage({
        level: 'warn',
        prefix: 'ProxyHandler',
        message: `检测到循环引用: ${path}`,
        context: path,
      })
    }
    // 返回缓存的结果（可能是最终结果，也可能是原对象（循环引用时））
    return cached as T
  }

  // 标记对象为正在处理中（临时缓存原对象，处理完成后会被替换为最终结果）
  // 这样可以检测循环引用：如果在递归过程中再次遇到这个对象，会返回原对象
  cache.set(obj as object, obj)

  // 处理 ref（优先检查 ref，因为 ref 的检查比 reactive 更快）
  if (isRef(obj)) {
    const unwrapped = unref(obj)
    const result = deepToRawInternal(unwrapped, finalConfig, path, depth + 1, startTime, cache) as T
    // 更新缓存：将 ref 对象映射到解包后的结果
    cache.set(obj as object, result)
    return result
  }

  // 处理 reactive 对象
  if (isReactive(obj)) {
    finalConfig.onProxyDetected(path, obj)

    if (finalConfig.warnInConsole) {
      logMessage({
        level: 'warn',
        prefix: 'ProxyHandler',
        message: `检测到 Proxy 对象: ${path}`,
        data: '建议使用 markRaw() 或重新设计数据结构以避免不必要的响应式包装',
        extra: obj,
      })
    }

    try {
      const rawObj = toRaw(obj)
      const result = deepToRawInternal(rawObj, finalConfig, path, depth + 1, startTime, cache)
      // 更新缓存：将 reactive 对象映射到转换后的结果
      cache.set(obj as object, result)
      return result as T
    } catch (error) {
      handleError(error as Error, path, finalConfig)
      // 转换失败时，缓存原对象
      cache.set(obj as object, obj)
      return obj // 转换失败时返回原对象
    }
  }

  // 处理数组
  if (Array.isArray(obj)) {
    if (!finalConfig.convertArray) {
      // 不转换数组时，缓存原数组
      cache.set(obj as object, obj)
      return obj
    }

    const result = obj.map((item, index) =>
      deepToRawInternal(item, finalConfig, buildPath(path, index), depth + 1, startTime, cache)) as T
    // 更新缓存：将数组映射到转换后的结果
    cache.set(obj as object, result)
    return result
  }

  // 处理普通对象
  if (finalConfig.deep) {
    // Error 对象直接返回，不转换其内部属性
    if (obj instanceof Error) {
      cache.set(obj as object, obj)
      return obj
    }

    const result: Record<string, unknown> = {}
    // 使用 Object.keys 替代 for...in，性能更好且更简洁
    // 注意：Object.keys 只返回可枚举的自有属性，对于大多数场景已经足够
    const keys = Object.keys(obj as object)

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const value = (obj as Record<string, unknown>)[key]
      const newPath = buildPath(path, key)

      try {
        if (finalConfig.skipKeys.includes(key)) {
          // 只剥一层，不往下 Object.keys：editor / provider 这类实例扫属性会卡死
          result[key] = isRef(value) ? unref(value) : toRaw(value)
          continue
        }
        result[key] = deepToRawInternal(value, finalConfig, newPath, depth + 1, startTime, cache)
      } catch (error) {
        handleError(error as Error, newPath, finalConfig)
        result[key] = value // 失败时保留原值
      }
    }

    const finalResult = result as T
    // 更新缓存：将对象映射到转换后的结果
    cache.set(obj as object, finalResult)
    return finalResult
  }

  // 不需要深度转换的对象，缓存原对象
  cache.set(obj as object, obj)
  return obj
}

export function deepToRaw<T = unknown>(
  obj: T,
  config: ProxyHandlerConfig = {},
  path: string = 'root',
  depth: number = 0,
  startTime: number = Date.now(),
  cache = new WeakMap<object, unknown>(),
): T {
  // 只在第一次调用时合并配置，避免递归中重复合并
  const finalConfig = mergeConfig(config)

  // 调用内部实现，传递已合并的配置和缓存 WeakMap
  // WeakMap 用于缓存已处理的对象，同时解决循环引用和重复处理问题
  return deepToRawInternal(obj, finalConfig, path, depth, startTime, cache)
}

/**
 * 检测对象中是否包含 Proxy 属性
 *
 * 这个函数会递归地扫描对象，找出所有 Vue 3 的响应式对象（reactive、ref）和只读属性。
 * 主要用于在更新数据前进行预检测，避免在 $patch 操作时遇到 Proxy 相关错误。
 *
 * @param obj - 要检测的对象，可以是任何类型
 * @param path - 当前检测的路径，用于标识 Proxy 对象的位置（默认: 'root'）
 * @param depth - 当前递归深度，用于性能保护（默认: 0）
 * @param visited - 已访问对象的 WeakSet，用于循环引用检测
 *
 * @returns 包含 Proxy 对象信息的数组，每个元素包含：
 *   - path: Proxy 对象的路径（如 'user.profile.avatar'）
 *   - value: Proxy 对象本身
 *   - type: Proxy 对象的类型（'reactive' | 'ref' | 'readonly'）
 *
 * @example
 * ```typescript
 * import { reactive, ref } from 'vue'
 * import { detectProxyProperties } from '@peerup/helper'
 *
 * // 基本用法
 * const data = {
 *   name: 'test',
 *   profile: reactive({ avatar: 'url' }),
 *   settings: ref({ theme: 'dark' })
 * }
 *
 * const proxyProps = detectProxyProperties(data)
 * console.log(proxyProps)
 * // 输出:
 * // [
 * //   { path: 'profile', value: Proxy {...}, type: 'reactive' },
 * //   { path: 'settings', value: RefImpl {...}, type: 'ref' }
 * // ]
 *
 * // 在更新前检测
 * const patchData = { user: reactive({ name: 'John' }) }
 * const detections = detectProxyProperties(patchData)
 *
 * if (detections.length > 0) {
 *   console.warn('数据包含 Proxy 对象，建议预处理')
 *   detections.forEach(({ path, type }) => {
 *     console.warn(`发现 ${type} 对象: ${path}`)
 *   })
 * }
 * ```
 *
 * @since 1.0.0
 */
export function detectProxyProperties(
  obj: unknown,
  path: string = 'root',
  depth: number = 0,
  visited = new WeakSet(),
): Array<{ path: string; value: unknown; type?: 'reactive' | 'ref' | 'readonly' }> {
  const results: Array<{ path: string; value: unknown; type?: 'reactive' | 'ref' | 'readonly' }> = []

  // 性能保护检查
  const guard = checkPerformanceGuard(obj, path, depth, Date.now())
  if (!guard.shouldContinue) {
    console.warn(`检测中断: ${guard.reason}`)
    return results
  }

  if (obj == null || typeof obj !== 'object') {
    return results
  }

  // 防止循环引用
  if (visited.has(obj)) {
    return results
  }

  visited.add(obj)

  if (isReactive(obj)) {
    results.push({ path, value: obj, type: getPropertyType(obj, path) })
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const itemResults = detectProxyProperties(item, buildPath(path, index), depth + 1, visited)
      results.push(...itemResults)
    })
  } else if (obj && typeof obj === 'object') {
    // 确保 obj 是对象类型后才能遍历
    const objRecord = obj as Record<string, unknown>
    for (const key in objRecord) {
      if (Object.hasOwn(objRecord, key)) {
        const value = objRecord[key]
        const newPath = buildPath(path, key)
        const itemResults = detectProxyProperties(value, newPath, depth + 1, visited)
        results.push(...itemResults)
      }
    }
  }

  return results
}
